import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { CSSResultGroup, html, LitElement, nothing, TemplateResult } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import typia, { IValidation } from 'typia'
import { tags } from 'typia'
import {
  BasketFormFinishedEvent,
  BasketFormResetEvent,
  BasketNameValidationErrorEvent
} from 'components/ordering/multiple-baskets/basket-events.js'
import {
  DELIVERY_POLICY_MUTATOR,
  DELIVERY_POLICY_STORE,
  DeliveryPolicyMutator,
  DeliveryPolicyStore,
} from 'components/ordering/multiple-baskets/basket-store/delivery-policy-store.js'
import { style } from './basket-form.style.js'
import { DeliveryPolicy, TrivialDeliveryPolicy } from 'components/ordering/acl/internal-types.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { createEffect } from '@cloudsuite/mosaic-shared/scripts/signals.js'


type Basket = {
  name: string & tags.MinLength<1>
  policy: string & tags.MinLength<1>
}

type Validity = {
  key: keyof Basket,
  valid: boolean,
  message: string,
}

type Translation = {
  key: string,
  invalid: string,
}

@customElement('csc-basket-form')
export class CSCBasketForm extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @property({ type: Number, attribute: 'party-id' })
  partyId = 0

  @property({ type: Number, attribute: 'shop-id' })
  shopId = 0

  @property({ attribute: 'basket-name' })
  basketName: string | null = null

  @property({ attribute: 'basket-delivery-policy', type: Object })
  private basketDeliveryPolicy: DeliveryPolicy | null = null

  @state()
  private defaultDeliveryPolicy: DeliveryPolicy = new TrivialDeliveryPolicy()

  get preSelectedDeliveryPolicy(): DeliveryPolicy {
    return this.basketDeliveryPolicy ?? this.defaultDeliveryPolicy
  }

  @state()
  private deliveryPolicies: DeliveryPolicy[] = []

  @state()
  private disableSubmitButton = false

  @query('form')
  private form!: HTMLFormElement

  @inject(DELIVERY_POLICY_MUTATOR)
  private deliveryPolicyMutator!: DeliveryPolicyMutator

  @inject(DELIVERY_POLICY_STORE)
  private deliveryPolicyStore!: DeliveryPolicyStore

  @state()
  validity: Validity[] = [
    {
      key: 'name',
      valid: true,
      message: ''
    }, {
      key: 'policy',
      valid: true,
      message: ''
    }
  ]

  translations: Record<string, Translation> = {
    name: {
      key: 'naam',
      invalid: t('basket_name_missing', { ns: 'csc-basket-form', default: 'You must enter a name' }).toString(),
    },
    basketNameTooLong: {
      key: 'mandje-naam-te-lang',
      invalid: t('basket_name_too_long', { ns: 'csc-basket-form', default: 'Name is too long' }).toString(),
    },
    policy: {
      key: 'policy',
      invalid: t('delivery_policy_missing', { ns: 'csc-basket-form', default: 'You must select a type' }).toString(),
    },
  }

  private formReset = () => {
    this.dispatchEvent(new BasketFormResetEvent())
  }

  private formSubmitted = async (event: SubmitEvent) => {
    event.preventDefault()
    this.disableSubmitButton = true

    const form = new FormData(this.form)
    const result = typia.validate<Basket>({
      name: form.get('basket-name'),
      policy: form.get('delivery-policy') ?? new TrivialDeliveryPolicy().name,
    })

    if (!result.success) {
      this.disableSubmitButton = false
      this.applyValidationErrors(result.errors)
      return
    }

    const policy = this.deliveryPolicies.find(policy => policy.name === result.data.policy)

    if (!policy) {
      this.disableSubmitButton = false
      return
    }

    this.dispatchEvent(new BasketFormFinishedEvent(result.data.name, policy))
    this.resetValidation()
  }

  private updateValidityField(error: IValidation.IError | undefined) {
    if (error) {
      return {
        valid: false,
        message: this.getMessage(error)
      }
    } else {
      return {
        valid: true,
        message: ''
      }
    }
  }

  constructor() {
    super()
    window.addEventListener(BasketNameValidationErrorEvent.type, this.handleServerSideValidationError.bind(this))
  }

  private handleServerSideValidationError(event: BasketNameValidationErrorEvent) {
    if (this.validity[0] && this.translations['basketNameTooLong']) {
      this.validity[0].valid = false
      this.validity[0].message = this.translations['basketNameTooLong'].invalid + ` (${event.reason.suppliedLength} > ${event.reason.maxLength})`
    }
    this.disableSubmitButton = false
    this.requestUpdate()
  }

  private getMessage(error: IValidation.IError) {
    const pathKey = error.path.split('.').pop()

    if (!pathKey) {
      return error.expected
    }

    const translation = this.translations[pathKey]

    if (!translation) {
      return error.expected
    }

    return translation.invalid
  }

  private applyValidationErrors(errors: IValidation.IError[]) {
    this.validity = this.validity.map(field => {
      const error = errors.find(error => error.path.endsWith(`.${field.key}`))
      return {
        key: field.key,
        ...this.updateValidityField(error)
      }
    })
  }

  private resetValidation() {
    this.validity = this.validity.map(field => ({
      ...field,
      valid: true,
      message: ''
    }))
  }

  private renderDeliveryPolicyRadioButtons = (policy: DeliveryPolicy) => {
    return html`
      <cse-radio-button
        id="r-${policy.name}-basket"
        value="${policy.name}"
        required>
        ${policy.name}
      </cse-radio-button>
    `
  }

  private renderPolicyInput = () => {
    return html`
      <cse-form-field>
        <cse-label slot="label" for="delivery-policy">
          ${t('delivery_policy_label', { ns: 'csc-basket-form', default: 'Basket type' })}
        </cse-label>
        <cse-radio-button-group
          name="delivery-policy"
          value="${this.preSelectedDeliveryPolicy.name}">
          ${this.deliveryPolicies.map((policy) => this.renderDeliveryPolicyRadioButtons(policy))}
        </cse-radio-button-group>
        ${this.validity[1] && !this.validity[1].valid
          ? html`
            <div id="invalid-policy" slot="invalid-text">
              ${this.validity[1].message}
            </div>`
          : nothing}
      </cse-form-field>
    `
  }

  private renderNameInput = (): TemplateResult => {
    return html`
      <cse-form-field>
        <cse-label slot="label" for="basket-name">
          ${t('basket_name_label', { ns: 'csc-basket-form', default: 'Basket name' })}
        </cse-label>
        <cse-text-input
          placeholder="${t('basket_name_placeholder', { ns: 'csc-basket-form', default: 'School year 1994 - 1995' })}"
          name="basket-name"
          value="${this.basketName ?? nothing}">
        </cse-text-input>
        ${this.validity[0] && !this.validity[0].valid
          ? html`
            <div id="invalid-name" slot="invalid-text">
              ${this.validity[0].message}
            </div>`
          : nothing}
      </cse-form-field>
    `
  }

  protected override render(): TemplateResult {
    return html`
      <form @submit="${this.formSubmitted}" @reset="${this.formReset}">
        <cse-fieldset class="basket-container">
          ${this.renderNameInput()}
          ${this.deliveryPolicies.length > 1 ? this.renderPolicyInput() : ''}
        </cse-fieldset>

        <div class="basket-buttons">
          <cse-button
            type="reset"
            fit="fill"
            variant="secondary">
            ${t('cancel_button', { ns: 'csc-basket-form', default: 'No, cancel' })}
          </cse-button>
          <cse-button
            type="submit"
            fit="fill"
            variant="primary"
            ?disabled="${this.disableSubmitButton}">
            ${t('submit_button', { ns: 'csc-basket-form', default: 'Yes, add' })}
          </cse-button>
        </div>
      </form>
    `
  }

  override connectedCallback(): void {
    super.connectedCallback()

    createEffect(() => {
      this.deliveryPolicies = this.deliveryPolicyStore.deliveryPolicies()
      this.defaultDeliveryPolicy = this.deliveryPolicyStore.defaultDeliveryPolicy()
    })

    this.deliveryPolicyMutator.initialize(this.partyId, this.shopId)
  }
}
